/**
 * CORE-1685 — утверждённый комплект эмодзи платформы, один на чаты, личные
 * сообщения, публикации и комментарии.
 *
 * Одиннадцать картинок из комплекта дизайна (`assets/reactions/agape_N.png`,
 * нумерация совпадает с файлами 1.png–11.png). Слаг хранит и принимает сервер
 * (CORE-1706): `agape_1`…`agape_11`, один и тот же на сообщениях ChatApi,
 * публикациях и комментариях. Системных эмодзи в интерфейсе нет — только
 * картинки; символ ниже лишь запасной текст: для подписей доступности,
 * превью в списках и мест, где картинку не нарисовать.
 *
 * ─── Старые пять ───
 *
 * До комплекта было пять слагов (AMB-64, CORE-1220): love, pray, fire,
 * seedling, sparkles. Сервер принимает их по-прежнему (их шлют сборки из
 * сторов) и отдаёт как есть, в новые не переводит. Здесь они сопоставлены по
 * смыслу и рисуются картинкой комплекта: нажатие по такому чипу ставит уже
 * новый слаг. Стримовские like/wow/sad/angry (`services/chat/reactions.ts`)
 * сопоставлены так же, чтобы история не рассыпалась на символы.
 *
 * ─── Почему слаг, а не картинка ───
 *
 * В базе лежит `type`, картинку когда-нибудь заменят, и это не должно быть
 * переписыванием строк в таблице. Поэтому картинка живёт только здесь.
 */
import type { ImageSourcePropType } from 'react-native';
import { reactionTypeOfUnicode } from './emojiAliases';

export interface BrandReaction {
  /** Слаг, как его хранит и принимает сервер. */
  type: string;
  /** Картинка комплекта. */
  image: ImageSourcePropType;
  /** Короткая русская подпись — палитра остаётся компактной. */
  label: string;
  /**
   * CORE-1790 — полный смысл по уточнению продукта (20.10.2026): подсказка
   * при наведении и подпись доступности. Один символ выражает несколько
   * близких смыслов, подпись их перечисляет.
   */
  hint: string;
  /** Запасной символ: подписи, превью, места без картинки. Не для интерфейса. */
  emoji: string;
}

/** Порядок значим: в нём реакции и показываются — как в комплекте. */
export const BRAND_REACTIONS: readonly BrandReaction[] = [
  { type: 'agape_1', image: require('../../assets/reactions/agape_1.png'), label: 'Любовь', hint: 'Любовь, тепло', emoji: '❤️' },
  { type: 'agape_2', image: require('../../assets/reactions/agape_2.png'), label: 'Баланс', hint: 'Баланс, гармония', emoji: '☯️' },
  { type: 'agape_3', image: require('../../assets/reactions/agape_3.png'), label: 'Благодарность', hint: 'Благодарность, просьба', emoji: '🙏' },
  { type: 'agape_4', image: require('../../assets/reactions/agape_4.png'), label: 'Огонь', hint: 'Огонь, горящее сердце', emoji: '🔥' },
  { type: 'agape_5', image: require('../../assets/reactions/agape_5.png'), label: 'Разбитое сердце', hint: 'Разбитое сердце, боль', emoji: '💔' },
  { type: 'agape_6', image: require('../../assets/reactions/agape_6.png'), label: 'Прозрение', hint: 'Прозрение, «смотрю»', emoji: '👁️' },
  { type: 'agape_7', image: require('../../assets/reactions/agape_7.png'), label: 'Покой', hint: 'Покой, практика, медитация', emoji: '🧘' },
  { type: 'agape_8', image: require('../../assets/reactions/agape_8.png'), label: 'Agape', hint: 'Agape, свет, вдохновение', emoji: '✨' },
  { type: 'agape_9', image: require('../../assets/reactions/agape_9.png'), label: 'Объятие', hint: 'Объятия, поддержка', emoji: '🤗' },
  { type: 'agape_10', image: require('../../assets/reactions/agape_10.png'), label: 'Несогласие', hint: 'Нет, против, несогласие, протест', emoji: '🙅' },
  { type: 'agape_11', image: require('../../assets/reactions/agape_11.png'), label: 'Грусть', hint: 'Грусть, слёзы', emoji: '😢' },
];

/**
 * CORE-1479 — наградные реакции: ладонь с сердцем, инь-ян и сердечко с
 * объятиями (выбор продукта 19.10). Видят их все, ставит только тот, у кого
 * они открыты, — за профиль, заполненный в первый месяц. Перечень присылает и
 * сервер (`user.rewardReactions.types`); этот — тот же самый, на случай ответа
 * без поля.
 */
export const REWARD_REACTION_TYPES: readonly string[] = ['agape_1', 'agape_2', 'agape_9'];

/**
 * Прежние слаги → комплект, по смыслу (решение владельца, 15.10):
 * ❤️ — ладонь с сердцем, 🙏 — молитвенные руки, 🔥 — горящее сердце,
 * 🌱 рост — медитирующий.
 *
 * CORE-1790 (уточнение продукта 20.10) — №10 это «нет, против, несогласие»,
 * а не «сила», поэтому положительный ✨ sparkles идёт к №8 (Agape, свет,
 * вдохновение), а стримовский angry — к №10, а не к разбитому сердцу.
 * Сохранённые `type` в истории не переписываются и счётчики не меняются:
 * таблица решает только, какой картинкой нарисовать старый слаг и какой слаг
 * поставит нажатие по такому чипу. Полная таблица совместимости:
 *
 *   love / heart / like → agape_1   pray → agape_3   fire → agape_4
 *   seedling → agape_7   sparkles → agape_8   wow → agape_6
 *   sad → agape_11   angry → agape_10
 */
export const LEGACY_REACTION_TYPES: Readonly<Record<string, string>> = {
  love: 'agape_1',
  heart: 'agape_1',
  like: 'agape_1',
  pray: 'agape_3',
  fire: 'agape_4',
  seedling: 'agape_7',
  sparkles: 'agape_8',
  wow: 'agape_6',
  sad: 'agape_11',
  angry: 'agape_10',
};

/** Слаг комплекта для любого известного слага; незнакомый — как есть. */
export function canonicalReactionType(type: string): string {
  return LEGACY_REACTION_TYPES[type] ?? type;
}

/**
 * CORE-1479 (возврат QA 23.10) — свои реакции под чипом `type`: все слаги
 * человека, которые рисуются той же картинкой комплекта.
 *
 * Чип показывает слаг комплекта, а у человека может стоять прежний: `love`
 * ставит сердечко «нравится» на карточке мастера (`likePost`) и сборки из
 * сторов. Для него это одна и та же ладонь с сердцем — своя, и снять её можно
 * всегда, даже когда поставить наградную уже нельзя. Снимается она тем слагом,
 * который реально стоит: сервер ищет строку по точному типу, и снятие
 * `agape_1` при стоящей `love` не снимает ничего.
 */
export function ownReactionSlugs(mine: Iterable<string> | null | undefined, type: string): string[] {
  const canonical = canonicalReactionType(type);
  const own: string[] = [];
  for (const slug of mine ?? []) {
    if (canonicalReactionType(slug) === canonical && !own.includes(slug)) own.push(slug);
  }
  return own;
}

/**
 * CORE-1479 (возврат QA 23.10) — реакции одной картинки в один чип: `love` и
 * `agape_1` рисуются одной ладонью, и два одинаковых чипа рядом читались бы
 * как две разные реакции. Счёт складывается, слаг чипа — слаг комплекта,
 * порядок — по первому появлению.
 */
export function groupByCanonicalReaction<T extends { type: string; count: number }>(items: Iterable<T>): T[] {
  const out: T[] = [];
  for (const item of items) {
    const type = canonicalReactionType(item.type);
    const i = out.findIndex((x) => x.type === type);
    if (i < 0) out.push({ ...item, type });
    else out[i] = { ...out[i], count: out[i].count + item.count };
  }
  return out;
}

/** Реакция комплекта по слагу (старые слаги — через сопоставление). */
export function reactionOf(type: string): BrandReaction | undefined {
  const canonical = canonicalReactionType(type);
  return BRAND_REACTIONS.find((r) => r.type === canonical);
}

/** Картинка комплекта для слага, или undefined — слаг нам не знаком. */
export function reactionImage(type: string): ImageSourcePropType | undefined {
  return reactionOf(type)?.image;
}

/** Запасной символ для слага, или undefined — сервер может знать тип, которого у нас нет. */
export function emojiOfReaction(type: string): string | undefined {
  return reactionOf(type)?.emoji;
}

/**
 * Слаг для обычного эмодзи. Нужен там, где выбор приходит символом: сначала
 * таблица алиасов (CORE-1790, целые последовательности, без учёта тона кожи
 * и селекторов), затем запасные символы комплекта.
 */
export function reactionTypeOfEmoji(emoji: string): string | undefined {
  return reactionTypeOfUnicode(emoji) ?? BRAND_REACTIONS.find((r) => r.emoji === emoji)?.type;
}
